import {
  FC,
  useState,
  useEffect,
  useRef,
  forwardRef,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import classNames from 'classnames/bind';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { FAIcon, IconStyle } from '../Icons/Icons';
import Tooltip, { TooltipPreference } from '../Tooltip/Tooltip';
import { filterIconTheme } from '../utils/ThemeFilter/ThemeFilter';
import styles from './Accordion.module.scss';

import { AccordionPropsTypes, AccordionTheme } from './types/propsType';
import { ControlIconType, KeyConstants, FontSize, AccordionStyle, BorderDesign, AccordionStatus } from './types/enums';

const cx = classNames.bind(styles);

const getStatusCls = (status: AccordionStatus) => {
  if (status === AccordionStatus.COMPLETE) {
    return IconStyle.SOLID;
  }
  return IconStyle.REGULAR;
};

const computeElement = ({ id, isAccordionOpen }: { id: string; isAccordionOpen: boolean }) => {
  const accordionBody = document.getElementById(`${id}-collapse`);
  if (accordionBody) {
    accordionBody.style.overflow = isAccordionOpen ? 'initial' : 'hidden';
  }
};

const getControlIcon = (isOpen = false, iconType: ControlIconType): IconName => {
  if (iconType === ControlIconType.CHEVRON) {
    return 'chevron-down';
  } else if (isOpen) {
    return 'minus';
  } else {
    return 'plus';
  }
};
/**
 * @description Accordion component .
 * Props ->
 * title: text in accordion (Required),
 * className: prop to get custom styling class,
 * iconType: prop to control icon type,
 * content: text prop to pass content body,
 * children: default component prop to inherit cutom HTML template,
 * accordionInfo: flag to control visibility of info tooltip,
 * accordionInfoText: tooltip text to display, defaults to title,
 * contentTitle: title for content body,
 * fontSize: 2 Font Variations Large & Small
 */

const Accordion: FC<AccordionPropsTypes> = forwardRef<HTMLButtonElement, AccordionPropsTypes>(
  (props: AccordionPropsTypes, ref) => {
    const {
      id,
      className = '',
      contentTitle = '',
      content = '',
      title,
      iconType = ControlIconType.CHEVRON,
      borderDesign,
      children,
      accordionInfo = false,
      accordionInfoText,
      onToggleCallback,
      isInCard = true,
      isOpen: isPropAccordionOpen,
      fontSize = FontSize.LARGE,
      isBorderDesign = true,
      labelInfo,
      status = AccordionStatus.INCOMPLETE,
      accordionStyle = AccordionStyle.DEFAULT,
      steppedBody,
      ...restProps
    } = props;

    const keys: string[] = [KeyConstants.ENTER, KeyConstants.SPACE];

    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const collapsibleRef = useRef<HTMLDivElement>(null);
    const timeourIntervalRef = useRef<NodeJS.Timeout>();
    const handleEvent = (e: ReactKeyboardEvent | ReactMouseEvent) => {
      const isOpen = !isAccordionOpen;
      clearTimeout(timeourIntervalRef.current);
      setIsAccordionOpen(isOpen);
      onToggleCallback?.(e, isOpen);
    };

    const shouldToggleOnKeyDown = (e: ReactKeyboardEvent) => {
      if (keys.includes(e.code)) {
        handleEvent(e);
      }
    };
    useEffect(() => {
      typeof isPropAccordionOpen === 'boolean' && setIsAccordionOpen(isPropAccordionOpen);
    }, [isPropAccordionOpen]);

    useEffect(() => {
      if (isAccordionOpen) {
        timeourIntervalRef.current = setTimeout(() => {
          computeElement({ id, isAccordionOpen });
        }, 300);
      } else {
        timeourIntervalRef.current && collapsibleRef.current?.classList.add(cx('close-content'));
        clearTimeout(timeourIntervalRef.current);
        computeElement({ id, isAccordionOpen });
      }
    }, [isAccordionOpen]);

    const styleCheck = accordionStyle !== AccordionStyle.STEPPED && (borderDesign || isBorderDesign);
    const CBorderDesignCls = cx({
      [borderDesign || BorderDesign.TOP_BORDER]: styleCheck,
      'card-accordion': !styleCheck && isInCard,
      'button-stepped-open': isAccordionOpen && accordionStyle === AccordionStyle.STEPPED,
    });
    const iconStyle = getStatusCls(status);
    const CClassname = cx('accordion-item', className, { show: isAccordionOpen }, CBorderDesignCls);
    const CBodyClassname = cx('collapse', { show: isAccordionOpen }, CBorderDesignCls);
    return (
      <div className={CClassname} id={id} role="region">
        <h2 className={`${styles['accordion-header']}`} id={`${id}-accordion-header`}>
          <button
            id={`${id}-button`}
            className={`${styles['accordion-button']} ${CBorderDesignCls}`}
            data-bs-toggle="collapse"
            data-bs-target={`${id}-collapse`}
            data-testid="accordion-test-id"
            aria-expanded={isAccordionOpen}
            aria-controls={`${id}-collapse`}
            ref={ref}
            onClick={handleEvent}
            onKeyDown={shouldToggleOnKeyDown}
            {...restProps}
          >
            <span className={styles['accordion-label-continer']}>
              <div className={styles['accordion-label-main']}>
                <div className={styles['accordion-label-left']}>
                  {accordionStyle === AccordionStyle.STEPPED && (
                    <FAIcon iconName="circle-check" iconStyle={iconStyle} className={styles[status]} />
                  )}
                  <span className={styles['accordion-label-body']}>
                    <label className={`${styles['accordion-label']} ${styles[fontSize]}`}>{labelInfo || title}</label>
                  </span>
                  {accordionInfo && (
                    <Tooltip
                      message={accordionInfoText || title}
                      preference={TooltipPreference.LEFT}
                      className={styles['icon-info']}
                    />
                  )}
                </div>
                <FAIcon
                  theme={filterIconTheme(restProps.theme)}
                  iconName={getControlIcon(isAccordionOpen, iconType)}
                  iconStyle={IconStyle.SOLID}
                  className={`${styles['icon']} ${styles['icon-right']} `}
                  data-testid="accordion-icon-testid"
                />
              </div>
              {accordionStyle === AccordionStyle.STEPPED && steppedBody && (
                <div className={styles['accordion-label-stepped-body']} data-testid="accordion-step-body">
                  {steppedBody}
                </div>
              )}
            </span>
          </button>
        </h2>
        <div
          id={`${id}-collapse`}
          className={CBodyClassname}
          aria-label={`${id}-accordion-header`}
          data-bs-parent={`#${id}`}
          ref={collapsibleRef}
        >
          <div className={`${styles['accordion-body']}`}>
            {content && contentTitle ? (
              <>
                <div className={styles['content-title']}>{contentTitle}</div>
                <div>{content}</div>
              </>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    );
  },
);
Accordion.displayName = 'Accordion';
export default Accordion;
export { ControlIconType, FontSize, AccordionStyle, BorderDesign, AccordionStatus };
export type { AccordionTheme };
